import { useState, useEffect, useCallback, useRef } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import {
  ServiceRequest,
  Patient,
  Practitioner,
  Specimen,
} from '@medplum/fhirtypes';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { OrderFormData } from '../components/OrderForm';
import { extractFormDataFromServiceRequest } from '../utils/fhir-helpers';

export interface OrderDetailsData {
  serviceRequest: ServiceRequest | null;
  patient: Patient | undefined;
  requester: Practitioner | undefined;
  specimens: Specimen[];
}

export function useOrderDetails(orderId?: string) {
  const medplum = useMedplum();
  const { t } = useTranslation('lims');
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [orderData, setOrderData] = useState<OrderDetailsData>({
    serviceRequest: null,
    patient: undefined,
    requester: undefined,
    specimens: [],
  });

  const loadOrderDetails = useCallback(async () => {
    if (!orderId) {
      return;
    }

    // Prevent concurrent calls
    if (loadingRef.current) {
      return;
    }
    setLoading(true);
    loadingRef.current = true;
    try {
      // Load ServiceRequest
      const serviceRequest = await medplum.readResource(
        'ServiceRequest',
        orderId
      );

      // Load Patient
      let patient: Patient | undefined;
      if (serviceRequest.subject?.reference) {
        const patientId = serviceRequest.subject.reference.replace(
          'Patient/',
          ''
        );
        patient = await medplum.readResource('Patient', patientId);
      }

      // Load Practitioner
      let requester: Practitioner | undefined;
      if (serviceRequest.requester?.reference) {
        const practitionerId = serviceRequest.requester.reference.replace(
          'Practitioner/',
          ''
        );
        requester = await medplum.readResource('Practitioner', practitionerId);
      }

      // Load all Specimens referenced in ServiceRequest.specimen array
      const specimens: Specimen[] = [];
      if (serviceRequest.specimen && serviceRequest.specimen.length > 0) {
        for (const specimenRef of serviceRequest.specimen) {
          try {
            if (specimenRef.reference?.startsWith('Specimen/')) {
              const specimenId = specimenRef.reference.replace('Specimen/', '');
              const specimen = await medplum.readResource(
                'Specimen',
                specimenId
              );
              specimens.push(specimen);
            }
          } catch (error) {
            console.error(
              'Failed to load specimen:',
              specimenRef.reference,
              error
            );
          }
        }
      }

      setOrderData({
        serviceRequest,
        patient,
        requester,
        specimens,
      });
    } catch (error: any) {
      notifications.show({
        title: t('notifications.order.loadError'),
        message: error.message || t('notifications.order.loadErrorMessage'),
        color: 'red',
      });
      throw error;
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [orderId, medplum, t]);
  const getFormDataFromOrder = useCallback((): Partial<OrderFormData> => {
    if (!orderData.serviceRequest) {
      return {};
    }

    const extractedData = extractFormDataFromServiceRequest(
      orderData.serviceRequest
    );
    return {
      ...extractedData,
      patient: orderData.patient,
      requester: orderData.requester,
    };
  }, [orderData.serviceRequest, orderData.patient, orderData.requester]);

  useEffect(() => {
    loadOrderDetails();
  }, [loadOrderDetails]);

  //Subscribe to ServiceRequest updates
  useSubscription(orderId ? `ServiceRequest/${orderId}` : '', (bundle) => {
    const updatedServiceRequest = bundle.entry?.find(
      (sr) => sr.resource?.resourceType === 'ServiceRequest'
    )?.resource as ServiceRequest | undefined;

    if (!updatedServiceRequest) {
      return;
    }

    // Update the service request in state
    setOrderData((prev) => ({
      ...prev,
      serviceRequest: updatedServiceRequest,
    }));
  });

  //Subscribe to Specimen changes for this order
  useSubscription(
    orderId && orderData.serviceRequest
      ? `Specimen?request=ServiceRequest/${orderId}`
      : '',
    (bundle) => {
      const updatedSpecimen = bundle.entry?.find(
        (s) => s.resource?.resourceType === 'Specimen'
      )?.resource as Specimen | undefined;

      if (!updatedSpecimen) {
        return;
      }

      //Update specimens list
      setOrderData((prev) => {
        const exists = prev.specimens.find((s) => s.id === updatedSpecimen.id);

        if (exists) {
          //Update existing specimen
          return {
            ...prev,
            specimens: prev.specimens.map((s) =>
              s.id === updatedSpecimen.id ? updatedSpecimen : s
            ),
          };
        } else {
          // Add new specimen
          return {
            ...prev,
            specimens: [...prev.specimens, updatedSpecimen],
          };
        }
      });
    }
  );

  return {
    ...orderData,
    loading,
    loadOrderDetails,
    getFormDataFromOrder,
  };
}
